import { useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useAtom } from 'jotai';
import { sortBy } from 'lodash';
import { useEffect, useMemo } from 'react';

import { LOG_PRODUCTS, PRODUCTS } from '@/constants/products.constants';
import useHandleError from '@/hooks/useHandleError';
import useProductsCheck from '@/hooks/useProductsCheck';

import { type ProductDTO, selectedOrganizationAtom } from '../Organizations';
import type { TicketMetricsByPriority } from '../ReportsTemplates/ReportsTemplate.types';
import {
  eventsPerDayAtom,
  mcscAtom,
  selectedMCSCAssessmentIdAtom,
  usageLimitsAtom,
  usagePerDayAtom
} from './Dashboard.store';
import type {
  EventsPerDayDTO,
  MyCyberScorecardDTO,
  ServiceUsagePerDayDTO,
  UsageLimitType
} from './Dashboard.types';
import { DEFAULT_MCSC_ASSESSMENT } from './dashboard.constants';

export const useFetchUsage = (orgProducts: ProductDTO[]) => {
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const [, setUsage] = useAtom(usagePerDayAtom);
  const handleError = useHandleError();

  const hasNetOrLogProducts = useMemo(() => {
    if (!orgProducts) return false;
    return orgProducts.some((p) =>
      LOG_PRODUCTS.includes(p.product_type as PRODUCTS)
    );
  }, [orgProducts]);

  const { isSuccess, isError, data, isFetching, error } = useQuery<
    ServiceUsagePerDayDTO[],
    AxiosError
  >({
    queryKey: [`organizations/${selectedOrganization.label}/service_usage`],
    enabled: selectedOrganization?.label !== 'all' && hasNetOrLogProducts,
    staleTime: 60 * 60 * 1000
  });

  useEffect(() => {
    if (isSuccess) {
      setUsage(data);
    }
    if (isError) {
      handleError(error, 'Failure to fetch usage data.');
      setUsage([]);
    }
  }, [isSuccess, isError, data]);

  return {
    isFetching
  };
};

export const useFetchUsageLimits = () => {
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const [, setUsageLimits] = useAtom(usageLimitsAtom);
  const handleError = useHandleError();

  const { data, isSuccess, isError, isFetching, error } = useQuery<
    UsageLimitType[],
    AxiosError
  >({
    queryKey: [
      `organizations/${selectedOrganization.label}/products/service_usage_limits`
    ],
    enabled:
      !!selectedOrganization.label && selectedOrganization.label !== 'all',
    staleTime: 60 * 60 * 1000
  });

  useEffect(() => {
    if (isSuccess) {
      setUsageLimits(data);
    }
    if (isError) {
      handleError(error, 'Failure to fetch usage limits.');
      setUsageLimits([]);
    }
  }, [isSuccess, isError, data]);

  return {
    isFetching
  };
};

export const useFetchEventsPerDay = () => {
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const [, setEvents] = useAtom(eventsPerDayAtom);
  const handleError = useHandleError();

  const { data, isSuccess, isError, error, isFetching } = useQuery<
    EventsPerDayDTO[],
    AxiosError
  >({
    queryKey: [`organizations/${selectedOrganization.label}/events/per_day`],
    staleTime: 60 * 60 * 1000
  });

  useEffect(() => {
    if (isSuccess) {
      setEvents(data);
    }
    if (isError) {
      handleError(error, 'Failure to fetch events data.');
    }
  }, [isSuccess, isError, data]);

  return {
    isFetching
  };
};

export const useFetchStatusTimes = (
  startDate: string | null,
  endDate: string | null
) => {
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const handleError = useHandleError();

  const queryKey = useMemo(() => {
    return [
      `organizations/${selectedOrganization.label}/cases/metrics/mttx?start_date=${startDate}&end_date=${endDate}&group_by_priority=true`
    ];
  }, [selectedOrganization, startDate, endDate]);

  const { data, isError, error } = useQuery<TicketMetricsByPriority[]>({
    queryKey,
    staleTime: 600000,
    enabled:
      selectedOrganization.label !== 'all' &&
      selectedOrganization.label !== 'noop' &&
      startDate !== null &&
      endDate !== null
    // Prevents status times from being fetched before the dashboardtimeframe is initialized
  });

  useEffect(() => {
    if (isError) {
      handleError(error as AxiosError, 'Failure to fetch status times.');
    }
  }, [isError]);

  return { data };
};

export const useFetchMCSC = () => {
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const [, setMCSC] = useAtom(mcscAtom);
  const [, setSelectedMCSCAssessmentId] = useAtom(selectedMCSCAssessmentIdAtom);
  const handleError = useHandleError();

  const hasMCSCProducts = useProductsCheck([PRODUCTS.MCSC]);

  const { data, isSuccess, isError, error, isFetching } = useQuery<
    MyCyberScorecardDTO[],
    AxiosError
  >({
    queryKey: [
      `organizations/${selectedOrganization.label}/my_cyber_scorecard`
    ],
    staleTime: 60 * 60 * 1000,
    enabled: selectedOrganization?.label !== 'all' && hasMCSCProducts
  });

  useEffect(() => {
    // If there are no MCSC products, set the MCSC to the default assessment
    if (!hasMCSCProducts) {
      setMCSC([DEFAULT_MCSC_ASSESSMENT]);
      setSelectedMCSCAssessmentId(null);
      return;
    }
    if (isError) {
      handleError(error, 'Failure to fetch MCSC data.');
    }
    if (isSuccess) {
      // If there are assessments, set the selected assessment to the first assessment
      if (data.length > 0) {
        const orderedAssessment = sortBy(data, 'created_at').reverse();

        const activeAssessments = orderedAssessment.filter(
          (assessment: MyCyberScorecardDTO) => assessment.status === 'active'
        );

        if (activeAssessments.length === 0 && data.length > 0) {
          // If there are no active assessments, set the selected assessment to the most recent assessment
          setSelectedMCSCAssessmentId(data[0].assessment_id);
        } else {
          // Set the selected assessment to the latest active assessment
          setSelectedMCSCAssessmentId(
            activeAssessments[0].assessment_id ?? null
          );
        }
      } else {
        setSelectedMCSCAssessmentId(null);
      }

      setMCSC(data);
    }
  }, [isSuccess, isError, data, hasMCSCProducts]);

  return {
    isFetching: hasMCSCProducts ? isFetching : false
  };
};
